import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import Api from "../../components/helpers/Api";
import SeoNoIndex from "../../components/helpers/SeoNoIndex";
import InjectedRegisterBillingForm from "../sales/CheckoutForm";
import { refreshAuthenticatedUser } from "../../components/helpers/AuthenticationHelpers";
import { AuthContext } from "../../components/helpers/AuthContext";

const FALLBACK_GROUP_PRICE = "£300";

const formatPence = (pence) =>
  pence ? `£${(pence / 100).toFixed(0)}` : FALLBACK_GROUP_PRICE;

const getCustomer = () => {
  const stored = sessionStorage.getItem("customer");
  return stored ? JSON.parse(stored) : null;
};

const inferPeriodDays = (daysRemaining) => daysRemaining > 60 ? 365 : 30;

const calcProration = (subscription, groupPricePence) => {
  if (!subscription?.next_billing_date || !subscription?.amount || !groupPricePence) return null;
  const msRemaining = new Date(subscription.next_billing_date) - new Date();
  if (msRemaining <= 0) return null;
  const daysRemaining = msRemaining / (1000 * 60 * 60 * 24);
  const periodDays = inferPeriodDays(daysRemaining);
  const creditPence = Math.round(subscription.amount * daysRemaining / periodDays);
  const amountDuePence = Math.max(0, groupPricePence - creditPence);
  const renewalDate = new Date();
  renewalDate.setFullYear(renewalDate.getFullYear() + 1);
  return { creditPence, amountDuePence, renewalDate, daysRemaining: Math.ceil(daysRemaining) };
};

const TeamUpgrade = () => {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const customer = getCustomer();
  const [teamName, setTeamName] = useState("");
  const [teamNameError, setTeamNameError] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("name");
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [intentType, setIntentType] = useState(null);
  const [groupPrice, setGroupPrice] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const plan = "year";

  useEffect(() => {
    Api.get("/payment/prices")
      .then((res) => setGroupPrice(res.data?.group?.annual ?? null))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const customerId = getCustomer()?.user?.externalCustomerId;
    if (!customerId) return;
    Api.post("/payment/get-subscription", { customerId })
      .then((res) => {
        const subs = Array.isArray(res.data) ? res.data : [];
        setCurrentSubscription(subs[0] ?? null);
      })
      .catch(() => {});
  }, []);

  if (!customer) {
    navigate("/sign-in");
    return null;
  }

  if (customer?.user?.accountRole === "MEMBER") {
    navigate("/your-profile/team");
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setTeamNameError("");

    if (!teamName.trim()) {
      setTeamNameError("Church or group name is required");
      return;
    }

    setLoading(true);

    try {
      await Api.post("/api/account", {
        name: teamName.trim(),
      });

      const subscriptionResponse = await Api.post("/payment/create-account-subscription", {
        plan,
        currency: "gbp",
        couponCode: "",
      });

      const { clientSecret: secret, intentType: intent } = subscriptionResponse.data;

      // Stripe auto-collected payment using the saved payment method — no UI step needed.
      if (intent === "complete") {
        const freshCustomer = await refreshAuthenticatedUser();
        if (freshCustomer) {
          login(freshCustomer);
        }
        setStep("confirmed");
        return;
      }

      sessionStorage.setItem("selectedAccountType", "team");
      sessionStorage.setItem("selectedPlan", plan);
      sessionStorage.setItem("clientSecret", secret);
      sessionStorage.setItem("intentType", intent);
      setClientSecret(secret);
      setIntentType(intent);
      setStep("billing");
    } catch (e) {
      if (e.response?.status === 409) {
        setError(
          <>
            You already have a group.{" "}
            <a href="/your-profile/team">Manage your group</a>
          </>
        );
      } else {
        setError("Unable to set up your group. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const renderCard = () => {
    if (step === "confirmed") {
      return (
        <Card className="card-lift--hover shadow border-0">
          <CardBody className="py-5">
            <h2 style={{ fontSize: "1rem" }} className="text-success text-uppercase">
              Account upgraded
            </h2>
            <div className="alert alert-success mt-3">
              Payment successfully taken. Your account has been upgraded to a Group account.
            </div>
            <Button color="primary" onClick={() => navigate("/your-profile/team")}>
              Go to your group
            </Button>
          </CardBody>
        </Card>
      );
    }

    if (step === "billing") {
      return (
        <Card className="card-lift--hover shadow border-0">
          <CardBody className="py-5">
            <h2 style={{ fontSize: "1rem" }} className="text-primary text-uppercase">
              Group subscription billing
            </h2>
            <p className="description mt-3">
              After payment, your group access will be activated automatically.
            </p>
            {clientSecret && intentType && <InjectedRegisterBillingForm />}
          </CardBody>
        </Card>
      );
    }

    return (
      <Card className="card-lift--hover shadow border-0">
        <CardBody className="py-5">
          <h2 style={{ fontSize: "1rem" }} className="text-primary text-uppercase">
            Set up your group
          </h2>
            <p className="description mt-3">
            Invite others to plan worship and share song-flows together. Group subscriptions are {formatPence(groupPrice)} per year for up to 10 members.
          </p>
          <div className="alert alert-info mt-3">
            Your existing saved payment method will be used — no new card details are required.
            To update your card details go <a href="/your-profile/billing-details">your billing details</a>
            page and click the Manage Billing button.
          </div>
          {error && <div className="alert alert-warning">{error}</div>}
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label for="teamName">Church or group name</Label>
              <InputGroup className="input-group-alternative mb-3">
                <InputGroupText>
                  <i className="ni ni-building" />
                </InputGroupText>
                <Input
                  id="teamName"
                  type="text"
                  maxLength="100"
                  value={teamName}
                  onChange={(e) => {
                    setTeamName(e.target.value);
                    setTeamNameError("");
                  }}
                />
              </InputGroup>
              <span className="text-danger">{teamNameError}</span>
            </FormGroup>
            {(() => {
              const fmt = (p) => `£${(p / 100).toFixed(2)}`;
              const fmtDate = (d) => d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
              const proration = calcProration(currentSubscription, groupPrice);
              if (proration) {
                return (
                  <div className="alert alert-info mt-3 mb-3">
                    <p className="mb-1"><strong>Upgrade cost breakdown</strong></p>
                    <p className="mb-1">Group subscription: <strong>{formatPence(groupPrice)}/year</strong></p>
                    <p className="mb-1">
                      Remaining credit from your current subscription ({proration.daysRemaining} days remaining):&nbsp;
                      <strong>−{fmt(proration.creditPence)}</strong>
                    </p>
                    <p className="mb-2">
                      Estimated due today: <strong>{fmt(proration.amountDuePence)}</strong>
                    </p>
                    <p className="mb-0 small">
                      Your new annual renewal date will be <strong>{fmtDate(proration.renewalDate)}</strong>.
                      The exact amount is confirmed at checkout and may vary slightly.
                    </p>
                  </div>
                );
              }
              return (
                <p className="description mt-3">
                  Billing: <strong>Annual — {formatPence(groupPrice)}/year</strong>
                </p>
              );
            })()}
            <Button color="primary" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Spinner size="sm" /> Authorise payment
                </>
              ) : (
                "Authorise payment"
              )}
            </Button>
          </Form>
        </CardBody>
      </Card>
    );
  };

  return (
    <>
      <SeoNoIndex noindex={true} />

      <div className="position-relative">
        <section className="section section-lg section-shaped pb-250">
          <div className="shape shape-style-1 shape-default">
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
          </div>
          <Container className="py-lg-md d-flex">
            <div className="col px-0">
              <Row>
                <Col lg="8">
                  <h1 className="display-3 text-white">
                    Building your song-flows{" "}
                    <span>set up your group</span>
                  </h1>
                </Col>
              </Row>
            </div>
          </Container>
          <div className="separator separator-bottom separator-skew">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="none"
              version="1.1"
              viewBox="0 0 2560 100"
              x="0"
              y="0"
            >
              <polygon className="fill-white" points="2560 0 2560 100 0 100" />
            </svg>
          </div>
        </section>
      </div>
      <section className="section section-lg pt-lg-0 mt--200">
        <Container>
          <Row className="justify-content-center">
            <Col lg="8">{renderCard()}</Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default TeamUpgrade;
